import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  diningBookingActionsArea,
  diningBookingActionsPermissionList,
  diningGuestCommunicationArea,
  diningGuestCommunicationPermissionList,
  diningManageLocationsArea,
  diningManageLocationsPermissionList,
  diningReportsArea,
  diningReportsPermissionList,
  diningSettingsAccessArea,
  diningSettingsAccessPermissionList,
} from "../../../constants/ReserveDiningUserPermissionsFields";
import {
  reserveListAvailableHeadingTooltip,
  reserveUserPermissionsTooltipHeader,
  reserveUserPermissionsTooltipPermissionArea,
  reserveUserPermissionsTooltipRoleSelect,
} from "../../../constants/ReserveTooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../styles/popupStyle.css";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import useScrollToTop from "../../hooks/useScrollToTop";

function PermissionToggleGroup({
  areaLabel,
  options,
  value,
  onChange,
  idPrefix,
}: {
  areaLabel: string;
  options: readonly string[];
  value: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}) {
  return (
    <Stack direction="column" width="100%" gap={1}>
      <FormLabel component="legend">
        {areaLabel}
        <Tooltip
          title={tooltipComponent(reserveUserPermissionsTooltipPermissionArea)}
        >
          <InfoIcon fontSize="small" />
        </Tooltip>
      </FormLabel>
      <ToggleButtonGroup
        value={value}
        onChange={(_event, next) => {
          onChange(
            Array.isArray(next) ? next : next != null ? [String(next)] : [],
          );
        }}
        aria-label={areaLabel}
        sx={{
          flexWrap: "wrap",
          gap: 0.75,
          justifyContent: "flex-start",
          "& .MuiToggleButtonGroup-grouped": {
            borderRadius: 1,
            m: 0,
          },
        }}
      >
        {options.map((opt) => (
          <ToggleButton
            key={opt}
            id={`${idPrefix}-${opt.replace(/\s+/g, "-").slice(0, 48)}`}
            value={opt}
            size="small"
            sx={{ textTransform: "none", py: 0.75 }}
          >
            {opt}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

function readPermissionArrayFromRow(
  row: any,
  areaKey: string,
  canonicalList: readonly string[],
): string[] {
  if (Array.isArray(row[areaKey])) {
    return [...row[areaKey]];
  }
  if (row?.permissionDetail && typeof row.permissionDetail === "object") {
    const d = row.permissionDetail[areaKey];
    const lab = [...canonicalList];
    if (d?.mode === "full") return lab;
    if (d?.mode === "none") return [];
    return lab.filter((l) => Boolean(d?.items?.[l]));
  }
  if (typeof row[areaKey] === "boolean" && row[areaKey]) {
    return [...canonicalList];
  }
  return [];
}

const ReserveUserPermissions = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>("");
  const [bookingActionsSel, setBookingActionsSel] = useState<string[]>([]);
  const [guestCommunicationSel, setGuestCommunicationSel] = useState<string[]>(
    [],
  );
  const [manageLocationsSel, setManageLocationsSel] = useState<string[]>([]);
  const [reportsSel, setReportsSel] = useState<string[]>([]);
  const [settingsAccessSel, setSettingsAccessSel] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [userPermissionsInformation, setUserPermissionsInformation] = useState<
    any[]
  >([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  useEffect(() => {
    if (fieldValues) {
      const rawPermissions = fieldValues[ReservePropertyEnum.userPermissions];
      setUserPermissionsInformation(
        Array.isArray(rawPermissions) ? rawPermissions : [],
      );
      const raw = fieldValues[ReservePropertyEnum.roles];
      setRolesList(
        Array.isArray(raw)
          ? raw.filter((r: any) => r && r["Role Code"] && r.Active !== false)
          : [],
      );
    }
  }, []);

  useEffect(() => {
    data(userPermissionsInformation);
    editClicked &&
      setSelectedId(
        checkData(userPermissionsInformation, selectedId) ? selectedId : null,
      );
  }, [userPermissionsInformation]);

  const validateInput = () => {
    if (!selectedRoleCode) {
      setAlertMessage("Select a role");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (!rolesList.some((r: any) => r["Role Code"] === selectedRoleCode)) {
      setAlertMessage(
        "Selected role is not available. Add or activate it on the Roles page.",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const editPermissions = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setSelectedRoleCode("");
      setBookingActionsSel([]);
      setGuestCommunicationSel([]);
      setManageLocationsSel([]);
      setReportsSel([]);
      setSettingsAccessSel([]);
      setEditClicked(false);
      return;
    }
    setSelectedRoleCode(list?.["Role Code"] ?? "");
    const row = list;
    if (!row) {
      return;
    }

    setBookingActionsSel(
      readPermissionArrayFromRow(
        row,
        diningBookingActionsArea,
        diningBookingActionsPermissionList,
      ),
    );
    setGuestCommunicationSel(
      readPermissionArrayFromRow(
        row,
        diningGuestCommunicationArea,
        diningGuestCommunicationPermissionList,
      ),
    );
    setManageLocationsSel(
      readPermissionArrayFromRow(
        row,
        diningManageLocationsArea,
        diningManageLocationsPermissionList,
      ),
    );
    setReportsSel(
      readPermissionArrayFromRow(
        row,
        diningReportsArea,
        diningReportsPermissionList,
      ),
    );
    setSettingsAccessSel(
      readPermissionArrayFromRow(
        row,
        diningSettingsAccessArea,
        diningSettingsAccessPermissionList,
      ),
    );
  };

  const deletePermissions = (id: number) => {
    setUserPermissionsInformation(
      deleteItem(userPermissionsInformation ?? [], id),
    );
  };

  const updatePermissions = () => {
    if (!validateInput()) return false;
    const selectedRoleName =
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(selectedRoleCode),
      )?.["Role Name"] as string | undefined) ?? "";
    const peers = (userPermissionsInformation ?? []).filter(
      (item: any) => String(item.id) !== String(selectedId),
    );
    if (
      checkDuplicate(peers, "Role Code", selectedRoleCode) ||
      checkDuplicate(peers, "Role Name", selectedRoleName)
    ) {
      setAlertMessage(
        "Cannot have duplicate entries for the same role (Role Code or Role Name).",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setUserPermissionsInformation((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((item: any) => {
        if (String(item.id) !== String(selectedId)) return item;
        const { permissionDetail: _omit, ...rest } = item;
        return { ...rest, ...payload, id: item.id };
      }),
    );
    editPermissions(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${ReservePropertyEnum.userPermissions} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addPermissions = () => {
    if (!validateInput()) {
      return;
    }
    const selectedRoleName =
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(selectedRoleCode),
      )?.["Role Name"] as string | undefined) ?? "";
    const peers = userPermissionsInformation ?? [];
    if (
      checkDuplicate(peers, "Role Code", selectedRoleCode) ||
      checkDuplicate(peers, "Role Name", selectedRoleName)
    ) {
      setAlertMessage(
        "Cannot have duplicate entries for the same role (Role Code or Role Name).",
      );
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setUserPermissionsInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      ),
    );
    setLastUpdatedRecord(payload);
    editPermissions(null);
    setEditClicked(false);
    setSelectedId(null);
  };

  const payload = {
    "Role Code": selectedRoleCode,
    "Role Name":
      (rolesList.find(
        (r: any) => String(r["Role Code"]) === String(selectedRoleCode),
      )?.["Role Name"] as string | undefined) ?? "",
    [diningBookingActionsArea]: bookingActionsSel,
    [diningGuestCommunicationArea]: guestCommunicationSel,
    [diningManageLocationsArea]: manageLocationsSel,
    [diningReportsArea]: reportsSel,
    [diningSettingsAccessArea]: settingsAccessSel,
  };

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.userPermissions}
              <Tooltip
                title={tooltipComponent(reserveUserPermissionsTooltipHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editPermissions(null);
                    setSelectedId(null);
                    setEditClicked(false);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editPermissions(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" width="100%" gap="20px">
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Role
                <Tooltip
                  title={tooltipComponent(
                    reserveUserPermissionsTooltipRoleSelect,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Stack direction="row" width="100%" gap="5px" alignItems="center">
                <FormControl fullWidth disabled={!rolesList.length}>
                  <InputLabel id="reserve-dining-perm-role-label">
                    Role
                  </InputLabel>
                  <Select
                    labelId="reserve-dining-perm-role-label"
                    id="reserve-dining-perm-role"
                    label="Role"
                    displayEmpty
                    value={selectedRoleCode || ""}
                    onChange={(e) =>
                      setSelectedRoleCode(e.target.value as string)
                    }
                    renderValue={(v) => {
                      if (!v) return "";
                      const r = rolesList.find(
                        (x: any) => String(x["Role Code"]) === String(v),
                      );
                      return r
                        ? `${r["Role Name"]} (${r["Role Code"]})`
                        : String(v);
                    }}
                    inputProps={{ "aria-label": "Role" }}
                  >
                    {!rolesList.length ? (
                      <MenuItem value="" disabled>
                        No roles defined — add roles on the Roles page first
                      </MenuItem>
                    ) : (
                      rolesList.map((r: any) => (
                        <MenuItem key={r["Role Code"]} value={r["Role Code"]}>
                          {r["Role Name"]} ({r["Role Code"]})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              gap={0.5}
            >
              <Typography variant="caption" color="text.secondary">
                Roles such as Host and Reservationist are maintained on the
                Roles page.
              </Typography>
            </Stack>

            <FormLabel
              component="legend"
              id="reserve-dining-perm-sections-label"
            >
              Dining permissions
              <Tooltip
                title={tooltipComponent(
                  reserveUserPermissionsTooltipPermissionArea,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>

            <Stack direction="column" width="100%" gap={2} pt={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Dining activity permissions for this role.
              </Typography>
              <PermissionToggleGroup
                areaLabel={diningBookingActionsArea}
                options={diningBookingActionsPermissionList}
                value={bookingActionsSel}
                onChange={setBookingActionsSel}
                idPrefix="reserve-dining-perm-booking-actions"
              />
              <PermissionToggleGroup
                areaLabel={diningGuestCommunicationArea}
                options={diningGuestCommunicationPermissionList}
                value={guestCommunicationSel}
                onChange={setGuestCommunicationSel}
                idPrefix="reserve-dining-perm-guest-communication"
              />
              <PermissionToggleGroup
                areaLabel={diningManageLocationsArea}
                options={diningManageLocationsPermissionList}
                value={manageLocationsSel}
                onChange={setManageLocationsSel}
                idPrefix="reserve-dining-perm-manage-locations"
              />
              <PermissionToggleGroup
                areaLabel={diningReportsArea}
                options={diningReportsPermissionList}
                value={reportsSel}
                onChange={setReportsSel}
                idPrefix="reserve-dining-perm-reports"
              />
              <PermissionToggleGroup
                areaLabel={diningSettingsAccessArea}
                options={diningSettingsAccessPermissionList}
                value={settingsAccessSel}
                onChange={setSettingsAccessSel}
                idPrefix="reserve-dining-perm-settings-access"
              />
            </Stack>
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updatePermissions() : addPermissions()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Permission Set
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.userPermissions} Available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(userPermissionsInformation) &&
              userPermissionsInformation.length > 0 ? (
                <DraggableItemList
                  itemData={userPermissionsInformation}
                  onOrderUpdate={(order) =>
                    setUserPermissionsInformation(order)
                  }
                  onEdit={(info) => {
                    editPermissions(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deletePermissions(id)}
                  code="Role Code"
                  name="Role Name"
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.userPermissions} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveUserPermissions;
